
  import {
    AppBar,
    Toolbar,
    Typography,
    Box,
    Avatar,
    Button,
    IconButton,
    Chip,
    Tooltip,
    Divider,
  } from "@mui/material";
  import { alpha, useTheme } from "@mui/material/styles";
  import useMediaQuery from "@mui/material/useMediaQuery";

  import { useAuth } from "../../Context/AuthContext";
  import { useNavigate } from "react-router-dom";
  import ExitToAppIcon from "@mui/icons-material/ExitToApp";
  import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
  import logo from "../../Assets/logo.png";

  export default function AdminHeader({ sidebarWidth = 0, anchor = "left" }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const handleLogout = () => {
      logout();
      navigate("/background/gate/login");
    };

    const offsetStyle = anchor === "left" ? { ml: `${sidebarWidth}px` } : { mr: `${sidebarWidth}px` };

    return (
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: `calc(100% - ${sidebarWidth}px)`,
          ...offsetStyle,
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
          color: "text.primary",
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
          boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.06)}`,
          backdropFilter: "blur(8px)",
          zIndex: (t) => t.zIndex.appBar,
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: { xs: 1.5, sm: 2.5 },
            minHeight: { xs: 56, sm: 64 },
          }}
        >
          {/* Logo + Brand */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box
              sx={{
                width: { xs: 36, sm: 42 },
                height: { xs: 36, sm: 42 },
                borderRadius: 2,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                flexShrink: 0,
              }}
            >
              <img
                src={logo}
                alt="Logo"
                style={{ width: "85%", height: "85%", objectFit: "contain" }}
              />
            </Box>
            {!isMobile && (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
                  Nuvvy
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                  لوحة الإدارة
                </Typography>
              </Box>
            )}
          </Box>

          {/* Right actions */}
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 0.75, sm: 1.5 } }}>
            {/* POS button */}
            <Button
              variant="contained"
              size={isMobile ? "small" : "medium"}
              startIcon={!isMobile && <PointOfSaleIcon />}
              onClick={() => navigate("/auth/pos-pages")}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                fontSize: { xs: "0.75rem", sm: "0.85rem" },
                px: { xs: 1.25, sm: 2 },
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.dark, 0.85)})`,
                boxShadow: `0 3px 10px ${alpha(theme.palette.primary.main, 0.35)}`,
                "&:hover": {
                  boxShadow: `0 5px 16px ${alpha(theme.palette.primary.main, 0.45)}`,
                },
              }}
            >
              {isMobile ? <PointOfSaleIcon fontSize="small" /> : "شاشة البيع"}
            </Button>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, opacity: 0.5 }} />

            {/* User info */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {!isMobile && (
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    {user?.name}
                  </Typography>
                  <Chip
                    label="مسؤول"
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ height: 18, fontSize: "0.68rem", fontWeight: 700, mt: 0.3 }}
                  />
                </Box>
              )}
              <Avatar
                sx={{
                  width: { xs: 34, sm: 38 },
                  height: { xs: 34, sm: 38 },
                  bgcolor: "primary.main",
                  fontWeight: 800,
                  fontSize: "1rem",
                  boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.4)}`,
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                }}
              >
                {user?.name?.charAt(0)}
              </Avatar>
            </Box>

            {/* Logout */}
            <Tooltip title="تسجيل الخروج" arrow>
              <IconButton
                onClick={handleLogout}
                aria-label="logout"
                size={isMobile ? "small" : "medium"}
                sx={{
                  color: "error.main",
                  border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
                  borderRadius: 2,
                  "&:hover": {
                    bgcolor: alpha(theme.palette.error.main, 0.08),
                    borderColor: "error.main",
                  },
                }}
              >
                <ExitToAppIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>
    );
  }
